import axios from 'axios';
import CSRFService from './csrf';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // Include cookies for CSRF
});

// Add token and CSRF headers to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  // Add CSRF token if needed
  const method = config.method?.toUpperCase() || 'GET';
  const url = config.url || '';
  
  if (CSRFService.needsCSRFProtection(method, url)) {
    const csrfHeaders = CSRFService.getCSRFHeaders();
    Object.assign(config.headers, csrfHeaders);
  }

  return config;
});

// Add response interceptor with automatic token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: any) => void;
  reject: (reason?: any) => void;
}> = [];

const MAX_QUEUE_SIZE = 100; // Prevent unbounded growth

const processQueue = (error: any, token?: string) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });
  
  failedQueue = [];
};

const addToQueue = (resolve: (value?: any) => void, reject: (reason?: any) => void) => {
  // Prevent unbounded queue growth
  if (failedQueue.length >= MAX_QUEUE_SIZE) {
    // Remove oldest entries and reject them
    const removed = failedQueue.splice(0, Math.floor(MAX_QUEUE_SIZE / 2));
    removed.forEach(({ reject }) => reject(new Error('Request queue overflow')));
  }
  
  failedQueue.push({ resolve, reject });
};

api.interceptors.response.use(
  (response) => {
    // Extract CSRF token from response headers if present
    const csrfToken = CSRFService.getTokenFromHeaders(response.headers);
    if (csrfToken) {
      CSRFService.setToken(csrfToken);
    }
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle CSRF token errors (403 with CSRF-related errors)
    if (error.response?.status === 403 && !originalRequest._csrfRetry) {
      const errorMessage = error.response?.data?.detail || '';
      const isCSRFError = error.response?.data?.code === 'CSRF_VALIDATION_FAILED' || 
                         errorMessage.includes('CSRF') || 
                         errorMessage.includes('csrf');
      
      if (isCSRFError) {
        console.log('CSRF validation failed, attempting to refresh token...');
        
        // Try to fetch a new CSRF token
        const newToken = await CSRFService.refreshToken();
        if (newToken) {
          originalRequest._csrfRetry = true;
          
          // Add the new CSRF token to the request
          const method = originalRequest.method?.toUpperCase() || 'GET';
          const url = originalRequest.url || '';
          
          if (CSRFService.needsCSRFProtection(method, url)) {
            const csrfHeaders = CSRFService.getCSRFHeaders();
            Object.assign(originalRequest.headers, csrfHeaders);
          }
          
          console.log('Retrying request with new CSRF token');
          return api(originalRequest);
        }
      }
    }

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          addToQueue(resolve, reject);
        }).then(token => {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }).catch(err => {
          return Promise.reject(err);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        const response = await authAPI.refresh();
        
        const { access_token } = response;
        processQueue(null, access_token);
        
        originalRequest.headers.Authorization = `Bearer ${access_token}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, undefined);
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        CSRFService.clearToken(); // Clear CSRF token on logout
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response.data?.detail || 'Insufficient permissions');
    } else if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data?.detail || 'Internal server error');
    }
    
    return Promise.reject(error);
  }
);

// Auth API with refresh token support
export const authAPI = {
  login: async (email: string, password: string) => {
    const formData = new FormData();
    formData.append('username', email);
    formData.append('password', password);
    
    const response = await api.post('/auth/token', formData);
    
    // Store both tokens
    if (response.data.access_token) {
      localStorage.setItem('access_token', response.data.access_token);
    }
    if (response.data.refresh_token) {
      localStorage.setItem('refresh_token', response.data.refresh_token);
    }
    
    return response.data;
  },
  
  refresh: async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    
    const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {
      refresh_token: refreshToken
    });
    
    // Update stored tokens
    localStorage.setItem('access_token', response.data.access_token);
    localStorage.setItem('refresh_token', response.data.refresh_token);
    
    return response.data;
  },
  
  logout: async () => {
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        await api.post('/auth/logout', {
          refresh_token: refreshToken
        });
      } catch (error) {
        console.error('Logout request failed:', error);
        // Continue with local logout even if server logout fails
      }
    }
    
    // Clear stored tokens
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
  },
  
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  }
};

// Public Registration API (no auth required)
export const publicAPI = {
  activateAccount: async (token: string, password: string) => {
    const response = await axios.post(`${API_BASE_URL}/auth/activate`, {
      token,
      password
    });
    return response.data;
  },

  activateAccountWithConsent: async (token: string, password: string, consentData: any) => {
    const response = await axios.post(`${API_BASE_URL}/auth/activate`, {
      token,
      password,
      consent: consentData
    });
    return response.data;
  },

  requestPasswordReset: async (email: string) => {
    const response = await axios.post(`${API_BASE_URL}/auth/reset-password-request`, {
      email
    });
    return response.data;
  },

  resetPassword: async (token: string, newPassword: string) => {
    const response = await axios.post(`${API_BASE_URL}/auth/reset-password`, {
      token,
      new_password: newPassword
    });
    return response.data;
  },

  validateToken: async (token: string, tokenType: 'activation' | 'reset') => {
    const response = await axios.post(`${API_BASE_URL}/auth/validate-token`, {
      token,
      token_type: tokenType
    });
    return response.data;
  }
};

// Chat API
export const chatAPI = {
  askQuestion: async (question: string, onProgress?: (progress: number) => void) => {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('access_token');
      
      const xhr = new XMLHttpRequest();
      
      // Track upload progress
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 50); // Up to 50% for upload
          onProgress?.(20 + progress);
        }
      });
      
      // Track download progress
      xhr.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const progress = Math.round((event.loaded / event.total) * 30); // 30% for download
          onProgress?.(70 + progress);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const response = JSON.parse(xhr.responseText);
            onProgress?.(100);
            resolve(response);
          } catch (error) {
            reject(new Error('Invalid response format'));
          }
        } else {
          reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`));
        }
      });
      
      xhr.addEventListener('error', () => {
        onProgress?.(0);
        reject(new Error('Network error occurred'));
      });
      
      xhr.addEventListener('timeout', () => {
        onProgress?.(0);
        reject(new Error('Request timeout'));
      });
      
      xhr.open('POST', `${API_BASE_URL}/chat/ask`);
      xhr.setRequestHeader('Content-Type', 'application/json');
      if (token) {
        xhr.setRequestHeader('Authorization', `Bearer ${token}`);
      }
      
      onProgress?.(10); // Starting request
      xhr.timeout = 60000; // 60 second timeout
      xhr.send(JSON.stringify({ question }));
    });
  },

  // Streaming version of askQuestion
  streamingChat: async (
    question: string,
    clinicalContext: string,
    onProgress?: (progress: number) => void,
    onRagChunk?: (chunk: string) => void,
    onBasicChunk?: (chunk: string) => void,
    onStageUpdate?: (stage: string, progress?: number) => void
  ) => {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        reject(new Error('Authentication required'));
        return;
      }

      // Use fetch with ReadableStream for streaming
      fetch(`${API_BASE_URL}/chat/ask-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ question })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        let ragContent = '';
        let basicContent = '';
        let conversationId = null;
        let buffer = '';

        const processStream = async (): Promise<void> => {
          try {
            const { done, value } = await reader.read();
            
            if (done) {
              // Process any remaining data in buffer
              if (buffer.trim()) {
                try {
                  const chunk = JSON.parse(buffer.trim());
                  if (chunk.type === 'complete') {
                    conversationId = chunk.metadata?.conversation_id;
                    onProgress?.(100);
                    onStageUpdate?.('Ukończono', 100);
                    
                    resolve({
                      conversation_id: conversationId,
                      answers: [
                        {
                          type: 'rag',
                          content: ragContent,
                          has_sources: chunk.metadata?.has_sources || false,
                          thinking_process: chunk.metadata?.rag_features?.thinking_process || null,
                          web_search_queries: chunk.metadata?.rag_features?.web_search_queries || null,
                          web_search_results: chunk.metadata?.rag_features?.web_search_results || null,
                          tool_calls: chunk.metadata?.rag_features?.tool_calls || null,
                          confidence_level: chunk.metadata?.rag_features?.confidence_level || null
                        },
                        {
                          type: 'basic',
                          content: basicContent,
                          has_sources: false,
                          thinking_process: chunk.metadata?.basic_features?.thinking_process || null,
                          web_search_queries: chunk.metadata?.basic_features?.web_search_queries || null,
                          web_search_results: chunk.metadata?.basic_features?.web_search_results || null,
                          tool_calls: chunk.metadata?.basic_features?.tool_calls || null,
                          confidence_level: chunk.metadata?.basic_features?.confidence_level || null
                        }
                      ]
                    });
                  }
                } catch (error) {
                  console.error('Error parsing final chunk:', error);
                }
              }
              return;
            }

            // Decode the chunk and add to buffer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            
            // Process complete lines
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer
            
            for (const line of lines) {
              if (line.trim()) {
                try {
                  const data = JSON.parse(line.trim());
                  
                  switch (data.type) {
                    case 'start':
                      onStageUpdate?.(data.content, 0);
                      onProgress?.(10);
                      break;
                      
                    case 'progress':
                      onStageUpdate?.(data.content, data.metadata?.progress);
                      onProgress?.(data.metadata?.progress || 0);
                      break;
                      
                    case 'rag_start':
                      onStageUpdate?.('Generuję odpowiedź RAG...', 60);
                      break;
                      
                    case 'basic_start':
                      onStageUpdate?.('Generuję podstawową odpowiedź...', 60);
                      break;
                      
                    case 'rag_chunk':
                      ragContent += data.content;
                      onRagChunk?.(data.content);
                      break;
                      
                    case 'basic_chunk':
                      basicContent += data.content;
                      onBasicChunk?.(data.content);
                      break;
                      
                    case 'complete':
                      conversationId = data.metadata?.conversation_id;
                      onProgress?.(100);
                      onStageUpdate?.('Ukończono', 100);
                      
                      resolve({
                        conversation_id: conversationId,
                        answers: [
                          {
                            type: 'rag',
                            content: ragContent,
                            has_sources: data.metadata?.has_sources || false,
                            thinking_process: data.metadata?.rag_features?.thinking_process || null,
                            web_search_queries: data.metadata?.rag_features?.web_search_queries || null,
                            web_search_results: data.metadata?.rag_features?.web_search_results || null,
                            tool_calls: data.metadata?.rag_features?.tool_calls || null,
                            confidence_level: data.metadata?.rag_features?.confidence_level || null
                          },
                          {
                            type: 'basic',
                            content: basicContent,
                            has_sources: false,
                            thinking_process: data.metadata?.basic_features?.thinking_process || null,
                            web_search_queries: data.metadata?.basic_features?.web_search_queries || null,
                            web_search_results: data.metadata?.basic_features?.web_search_results || null,
                            tool_calls: data.metadata?.basic_features?.tool_calls || null,
                            confidence_level: data.metadata?.basic_features?.confidence_level || null
                          }
                        ]
                      });
                      return;
                      
                    case 'error':
                      onStageUpdate?.('Błąd: ' + data.content, 0);
                      reject(new Error(data.content));
                      return;
                  }
                  
                } catch (error) {
                  console.error('Error parsing chunk:', error);
                }
              }
            }
            
            await processStream();
          } catch (error) {
            reject(error);
          }
        };

        processStream().catch(reject);
      })
      .catch(reject);
    });
  },
  
  selectAnswer: async (conversationId: number, choice: 'rag' | 'basic') => {
    const response = await api.post(`/chat/conversations/${conversationId}/select`, { choice });
    return response.data;
  },
  
  submitFeedback: async (conversationId: number, feedback: string) => {
    const response = await api.post(`/chat/conversations/${conversationId}/feedback`, { feedback });
    return response.data;
  },
  
  getConversationHistory: async () => {
    const response = await api.get('/chat/conversations');
    return response.data;
  },
  
  getUserProfile: async () => {
    const response = await api.get('/chat/profile');
    return response.data;
  },

  // Clinical History Methods
  getClinicalHistory: async () => {
    const response = await api.get('/clinical/clinical-history');
    return response.data;
  },

  getCurrentClinicalInfo: async () => {
    const response = await api.get('/clinical/clinical-current');
    return response.data;
  },

  addOwnClinicalHistory: async (clinicalInfo: string, notes?: string, aiImprovement?: boolean) => {
    const response = await api.post('/clinical/clinical-history', {
      clinical_info: clinicalInfo,
      notes: notes,
      ai_improvement: aiImprovement || false
    });
    return response.data;
  },

  previewAIImprovement: async (clinicalInfo: string) => {
    const response = await api.post('/clinical/clinical-preview-ai-improvement', {
      clinical_info: clinicalInfo,
      ai_improvement: true
    });
    return response.data;
  }
};

// Admin API
export const adminAPI = {
  getPatients: async (skip = 0, limit = 50) => {
    const response = await api.get(`/admin/patients?skip=${skip}&limit=${limit}`);
    return response.data;
  },
  
  createPatient: async (patientData: {
    email: string;
    password: string;
    full_name: string;
    clinical_info?: string;
    doctor_id?: number;
  }) => {
    const response = await api.post('/admin/patients', patientData);
    return response.data;
  },
  
  getPatientConversations: async (patientId: number, skip = 0, limit = 20) => {
    const response = await api.get(`/admin/patients/${patientId}/conversations?skip=${skip}&limit=${limit}`);
    return response.data;
  },
  
  getAnswerStatistics: async (startDate?: string, endDate?: string) => {
    let url = '/admin/statistics/answer-preferences';
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (params.toString()) url += `?${params.toString()}`;
    
    const response = await api.get(url);
    return response.data;
  },
  
  updateClinicalInfo: async (patientId: number, clinicalInfo: string) => {
    const response = await api.put(`/admin/patients/${patientId}/clinical-info`, {}, {
      params: { clinical_info: clinicalInfo }
    });
    return response.data;
  },

  // Clinical History Management (Admin)
  getUserClinicalHistory: async (userId: number) => {
    const response = await api.get(`/clinical/clinical-history/${userId}`);
    return response.data;
  },

  addClinicalHistoryEntry: async (userId: number, clinicalInfo: string, notes?: string, aiImprovement?: boolean) => {
    const response = await api.post(`/clinical/clinical-history/${userId}`, {
      user_id: userId,
      clinical_info: clinicalInfo,
      notes: notes,
      ai_improvement: aiImprovement || false
    });
    return response.data;
  },

  previewAIImprovement: async (clinicalInfo: string) => {
    const response = await api.post('/clinical/clinical-preview-ai-improvement', {
      clinical_info: clinicalInfo,
      ai_improvement: true
    });
    return response.data;
  },

  // System Settings
  getSystemSettings: async () => {
    const response = await api.get('/admin/settings');
    return response.data;
  },

  updateSystemSettings: async (settings: any) => {
    const response = await api.put('/admin/settings', settings);
    return response.data;
  },

  testSMTPConnection: async (smtpConfig: any) => {
    const response = await api.post('/admin/settings/test-smtp', smtpConfig);
    return response.data;
  },

  // Enhanced Patient Registration
  createPatientWithInvite: async (patientData: {
    email: string;
    full_name: string;
    clinical_info?: string;
    doctor_id?: number;
  }) => {
    const response = await api.post('/admin/patients/invite', patientData);
    return response.data;
  },

  resendInvitation: async (patientId: number) => {
    const response = await api.post(`/admin/patients/${patientId}/resend-invite`);
    return response.data;
  },

  resendDoctorInvitation: async (doctorId: number) => {
    const response = await api.post(`/admin/doctors/${doctorId}/resend-invite`);
    return response.data;
  },

  regenerateActivationToken: async (patientId: number) => {
    const response = await api.post(`/admin/patients/${patientId}/regenerate-activation`);
    return response.data;
  },

  suspendPatient: async (patientId: number) => {
    const response = await api.post(`/admin/patients/${patientId}/suspend`);
    return response.data;
  },

  unsuspendPatient: async (patientId: number) => {
    const response = await api.post(`/admin/patients/${patientId}/unsuspend`);
    return response.data;
  },

  getPendingPatients: async () => {
    const response = await api.get('/admin/patients/pending');
    return response.data;
  },

  // Doctor Management (Admin Only)
  getDoctors: async (skip = 0, limit = 50) => {
    const response = await api.get(`/admin/doctors?skip=${skip}&limit=${limit}`);
    return response.data;
  },

  createDoctor: async (doctorData: {
    email: string;
    password: string;
    full_name: string;
  }) => {
    const response = await api.post('/admin/doctors', doctorData);
    return response.data;
  },

  inviteDoctor: async (doctorData: {
    email: string;
    full_name: string;
  }) => {
    const response = await api.post('/admin/doctors/invite', doctorData);
    return response.data;
  },

  updateDoctor: async (doctorId: number, doctorData: {
    full_name?: string;
    is_active?: boolean;
  }) => {
    const response = await api.put(`/admin/doctors/${doctorId}`, doctorData);
    return response.data;
  },

  deleteDoctor: async (doctorId: number) => {
    const response = await api.delete(`/admin/doctors/${doctorId}`);
    return response.data;
  },

  assignPatientToDoctor: async (patientId: number, doctorId?: number) => {
    const response = await api.put(`/admin/patients/${patientId}/assign-doctor`, {
      doctor_id: doctorId
    });
    return response.data;
  },

  // NOTE: Knowledge Base / TxtAI / Qdrant management removed - now using AI Consilium service
  // See /api/admin/ai-consilium-status for service status

  // Security Management
  getSecurityDashboard: async (days = 7) => {
    const response = await api.get(`/admin/security/dashboard?days=${days}`);
    return response.data;
  },

  getAuditLogs: async (page = 1, limit = 50, filters: {
    event_type?: string;
    risk_level?: string;
    ip_address?: string;
    user_id?: number;
    days?: number;
  } = {}) => {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...Object.fromEntries(
        Object.entries(filters).map(([key, value]) => [key, value?.toString() || ''])
      )
    });
    const response = await api.get(`/admin/security/audit-logs?${params}`);
    return response.data;
  },

  getBlockedIPs: async (includeExpired = false) => {
    const response = await api.get(`/admin/security/blocked-ips?include_expired=${includeExpired}`);
    return response.data;
  },

  blockIP: async (ipAddress: string, reason: string, permanent = false, hours = 24) => {
    const response = await api.post('/admin/security/block-ip', {
      ip_address: ipAddress,
      reason,
      permanent,
      hours
    });
    return response.data;
  },

  unblockIP: async (ipAddress: string, reason = 'Admin override') => {
    const response = await api.post('/admin/security/unblock-ip', {
      ip_address: ipAddress,
      reason
    });
    return response.data;
  },

  getSecurityStatistics: async (days = 30) => {
    const response = await api.get(`/admin/security/statistics?days=${days}`);
    return response.data;
  },

  getEventTypes: async () => {
    const response = await api.get('/admin/security/event-types');
    return response.data;
  },

  cleanupSecurityLogs: async (daysToKeep = 90) => {
    const response = await api.delete(`/admin/security/cleanup?days_to_keep=${daysToKeep}`);
    return response.data;
  },

  // New comprehensive API logging endpoints
  getApiUsageStatistics: async (days = 7) => {
    const response = await api.get(`/admin/security/api-usage-statistics?days=${days}`);
    return response.data;
  },

  getUserActivity: async (userId?: number, days = 7, page = 1, limit = 50) => {
    const params = new URLSearchParams({
      days: days.toString(),
      page: page.toString(),
      limit: limit.toString()
    });
    if (userId) {
      params.append('user_id', userId.toString());
    }
    const response = await api.get(`/admin/security/user-activity?${params}`);
    return response.data;
  },

  getSecuritySummary: async (days = 7) => {
    const response = await api.get(`/admin/security/security-summary?days=${days}`);
    return response.data;
  },

  // PDF Downloads
  downloadInvitationPDF: async (patientId: number) => {
    const response = await api.get(`/admin/patients/${patientId}/invitation-pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  downloadConsentPDF: async (patientId: number) => {
    const response = await api.get(`/admin/patients/${patientId}/consent-pdf`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Test PDF Generation
  generateTestInvitationPDF: async () => {
    const response = await api.post('/admin/test/invitation-pdf', {}, {
      responseType: 'blob'
    });
    return response.data;
  },

  generateTestConsentPDF: async () => {
    const response = await api.post('/admin/test/consent-pdf', {}, {
      responseType: 'blob'
    });
    return response.data;
  }

  // NOTE: Qdrant management removed - now using AI Consilium service
};

// Toolbox API
export const toolboxAPI = {
  // AI Chat
  chat: async (question: string, clinicalContext: string, patientId?: number) => {
    const response = await api.post('/toolbox/chat', {
      question,
      clinical_context: clinicalContext,
      patient_id: patientId
    });
    return response.data;
  },

  // Streaming version of chat
  chatStreaming: async (
    question: string,
    clinicalContext: string,
    patientId?: number,
    onProgress?: (progress: number) => void,
    onRagChunk?: (chunk: string) => void,
    onBasicChunk?: (chunk: string) => void,
    onStageUpdate?: (stage: string, progress?: number) => void
  ) => {
    return new Promise((resolve, reject) => {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        reject(new Error('Authentication required'));
        return;
      }

      // Use fetch with ReadableStream for streaming
      fetch(`${API_BASE_URL}/chat/toolbox/chat-stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          question,
          clinical_context: clinicalContext,
          patient_id: patientId
        })
      })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        if (!response.body) {
          throw new Error('No response body');
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        let ragContent = '';
        let basicContent = '';
        let buffer = '';

        const processStream = async (): Promise<void> => {
          try {
            const { done, value } = await reader.read();
            
            if (done) {
              // Process any remaining data in buffer
              if (buffer.trim()) {
                try {
                  const chunk = JSON.parse(buffer.trim());
                  if (chunk.type === 'complete') {
                    onProgress?.(100);
                    onStageUpdate?.('Ukończono', 100);
                    
                    resolve({
                      session_id: chunk.metadata?.session_id || 'streaming_session',
                      answers: [
                        {
                          type: 'rag',
                          content: ragContent,
                          has_sources: chunk.metadata?.has_sources || false,
                          thinking_process: chunk.metadata?.rag_features?.thinking_process || null,
                          web_search_queries: chunk.metadata?.rag_features?.web_search_queries || null,
                          web_search_results: chunk.metadata?.rag_features?.web_search_results || null,
                          tool_calls: chunk.metadata?.rag_features?.tool_calls || null,
                          confidence_level: chunk.metadata?.rag_features?.confidence_level || null
                        },
                        {
                          type: 'basic',
                          content: basicContent,
                          has_sources: false,
                          thinking_process: chunk.metadata?.basic_features?.thinking_process || null,
                          web_search_queries: chunk.metadata?.basic_features?.web_search_queries || null,
                          web_search_results: chunk.metadata?.basic_features?.web_search_results || null,
                          tool_calls: chunk.metadata?.basic_features?.tool_calls || null,
                          confidence_level: chunk.metadata?.basic_features?.confidence_level || null
                        }
                      ],
                      response_time_ms: chunk.metadata?.response_time_ms || 0,
                      rag_sources: chunk.metadata?.rag_sources || ''
                    });
                  }
                } catch (error) {
                  console.error('Error parsing final chunk:', error);
                }
              }
              return;
            }

            // Decode the chunk and add to buffer
            const chunk = decoder.decode(value, { stream: true });
            buffer += chunk;
            
            // Process complete lines
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer
            
            for (const line of lines) {
              if (line.trim()) {
                try {
                  const data = JSON.parse(line.trim());
                  
                  switch (data.type) {
                    case 'start':
                      onStageUpdate?.(data.content, 0);
                      onProgress?.(10);
                      break;
                      
                    case 'progress':
                      onStageUpdate?.(data.content, data.metadata?.progress);
                      onProgress?.(data.metadata?.progress || 0);
                      break;
                      
                    case 'rag_start':
                      onStageUpdate?.('Generuję odpowiedź RAG...', 60);
                      break;
                      
                    case 'basic_start':
                      onStageUpdate?.('Generuję podstawową odpowiedź...', 60);
                      break;
                      
                    case 'rag_chunk':
                      ragContent += data.content;
                      onRagChunk?.(data.content);
                      break;
                      
                    case 'basic_chunk':
                      basicContent += data.content;
                      onBasicChunk?.(data.content);
                      break;
                      
                    case 'complete':
                      onProgress?.(100);
                      onStageUpdate?.('Ukończono', 100);
                      
                      resolve({
                        session_id: data.metadata?.session_id || 'streaming_session',
                        answers: [
                          {
                            type: 'rag',
                            content: ragContent,
                            has_sources: data.metadata?.has_sources || false,
                            thinking_process: data.metadata?.rag_features?.thinking_process || null,
                            web_search_queries: data.metadata?.rag_features?.web_search_queries || null,
                            web_search_results: data.metadata?.rag_features?.web_search_results || null,
                            tool_calls: data.metadata?.rag_features?.tool_calls || null,
                            confidence_level: data.metadata?.rag_features?.confidence_level || null
                          },
                          {
                            type: 'basic',
                            content: basicContent,
                            has_sources: false,
                            thinking_process: data.metadata?.basic_features?.thinking_process || null,
                            web_search_queries: data.metadata?.basic_features?.web_search_queries || null,
                            web_search_results: data.metadata?.basic_features?.web_search_results || null,
                            tool_calls: data.metadata?.basic_features?.tool_calls || null,
                            confidence_level: data.metadata?.basic_features?.confidence_level || null
                          }
                        ],
                        response_time_ms: data.metadata?.response_time_ms || 0,
                        rag_sources: data.metadata?.rag_sources || ''
                      });
                      return;
                      
                    case 'error':
                      onStageUpdate?.('Błąd: ' + data.content, 0);
                      reject(new Error(data.content));
                      return;
                  }
                  
                } catch (error) {
                  console.error('Error parsing chunk:', error);
                }
              }
            }
            
            await processStream();
          } catch (error) {
            reject(error);
          }
        };

        processStream().catch(reject);
      })
      .catch(reject);
    });
  },

  selectAnswer: async (sessionId: string, selectedAnswer: string) => {
    const response = await api.post('/toolbox/chat/select-answer', {
      session_id: sessionId,
      selected_answer: selectedAnswer
    });
    return response.data;
  },

  // Submit feedback
  submitFeedback: async (sessionId: string, feedback: string) => {
    const response = await api.post('/toolbox/feedback', {
      session_id: sessionId,
      feedback: feedback
    });
    return response.data;
  },

  // Clinical Improvement
  improveClinicalContext: async (currentContext: string) => {
    const response = await api.post('/toolbox/clinical-improvement', {
      current_context: currentContext
    });
    return response.data;
  },

  // Patient Data
  getMyPatients: async () => {
    const response = await api.get('/toolbox/my-patients');
    return response.data;
  },

  getPatientClinicalContext: async (patientId: number) => {
    const response = await api.get(`/toolbox/patients/${patientId}/clinical-context`);
    return response.data;
  },

  // History and Statistics
  getHistory: async (days: number = 30, toolType?: string, page: number = 1, limit: number = 50) => {
    const params = new URLSearchParams();
    params.append('days', days.toString());
    params.append('page', page.toString());
    params.append('limit', limit.toString());
    
    if (toolType) {
      params.append('tool_type', toolType);
    }
    
    const response = await api.get(`/toolbox/history?${params.toString()}`);
    return response.data;
  },

  getStatistics: async (days: number = 30) => {
    const response = await api.get(`/toolbox/statistics?days=${days}`);
    return response.data;
  }
};

export default api;
